import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Tile,
  Button,
  TextInput,
  InlineNotification,
  Loading,
  Tag,
  ContentSwitcher,
  Switch,
} from '@carbon/react';
import { CheckmarkFilled, Pen, DocumentPdf, ArrowLeft } from '@carbon/icons-react';
import { getDocumentByToken, signDocument } from '@/lib/documentsApi';
import {
  buildSignedContractText,
  createContractPdfBlobUrl,
  createContractPdfDataUrl,
  downloadContractPdf,
  revokePdfBlobUrl,
} from '@/lib/contractPdf';

const ContractSignPage = () => {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(0);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const canvasRef = useRef(null);

  useEffect(() => {
    getDocumentByToken(token)
      .then((d) => {
        setDoc(d);
        setSignerName(d.clientName || d.data?.contractData?.clientSigner || '');
      })
      .catch(() => setError('Contract not found or link expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const content = doc?.data?.signedContent || doc?.data?.content || '';
  const isSigned = doc?.status === 'signed';
  const title = doc?.data?.templateName || 'Contract for Signature';
  const companySignature = doc?.data?.companySignature || null;
  const companySigner =
    doc?.data?.contractData?.companySigner || doc?.data?.contractData?.companyName || '';

  const pdfOptions = useMemo(() => {
    if (!doc) return null;
    return {
      title,
      content: doc.data?.content || '',
      companySignature,
      companySigner,
      clientSignature: isSigned ? doc.clientSignature || doc.data?.clientSignature : null,
      clientSigner: isSigned ? doc.signerName || doc.data?.signerName : null,
      signerName: isSigned ? doc.signerName : null,
      signedAt: isSigned ? doc.signedAt : null,
    };
  }, [doc, isSigned, title, companySignature, companySigner]);

  useEffect(() => {
    if (!pdfOptions) return;
    if (isSigned && doc?.data?.pdfDataUrl) {
      setPdfPreviewUrl(doc.data.pdfDataUrl);
      return;
    }
    const url = createContractPdfBlobUrl(pdfOptions);
    setPdfPreviewUrl(url);
    return () => revokePdfBlobUrl(url);
  }, [pdfOptions, isSigned, doc?.data?.pdfDataUrl]);

  useEffect(() => {
    if (!canvasRef.current || isSigned) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, [doc, isSigned]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  const handleSign = async () => {
    if (!signatureData || !signerName || !doc) return;
    setSubmitting(true);
    setError('');
    try {
      const signedContent = buildSignedContractText(doc.data?.content || '', {
        companySigner,
        clientSigner: signerName,
        signerName,
        signedAt: new Date().toISOString(),
      });
      const pdfDataUrl = createContractPdfDataUrl({
        title,
        content: doc.data?.content || '',
        companySignature,
        companySigner,
        clientSignature: signatureData,
        clientSigner: signerName,
        signerName,
        signedAt: new Date().toISOString(),
      });
      const updated = await signDocument(token, {
        signerName,
        signatureData,
        signedContent,
        pdfDataUrl,
      });
      setDoc(updated);
    } catch (err) {
      setError(err.message || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadSignedPdf = () => {
    if (!pdfOptions) return;
    downloadContractPdf(
      `${title.replace(/\s+/g, '_')}_signed.pdf`,
      pdfOptions
    );
  };

  if (loading) {
    return (
      <div className="public-page loading-screen">
        <Loading withOverlay={false} description="Loading contract..." />
      </div>
    );
  }

  if (error && !doc) {
    return (
      <div className="public-page">
        <Tile>
          <InlineNotification kind="error" title="Error" subtitle={error || 'Not found'} hideCloseButton />
          <Link to="/">← Back to SenseStack</Link>
        </Tile>
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="public-header">
        <Link to="/" className="public-brand">SenseStack</Link>
        <Tag type={isSigned ? 'green' : 'blue'}>{isSigned ? 'Signed' : 'Awaiting Signature'}</Tag>
      </div>

      <Button kind="ghost" renderIcon={ArrowLeft} as={Link} to="/" className="public-back-btn">
        Back to SenseStack
      </Button>

      {error && (
        <InlineNotification kind="error" title="Error" subtitle={error} hideCloseButton className="contract-notification" />
      )}

      <Tile className="public-document-tile">
        <h1>{title}</h1>
        <p className="public-meta">
          Prepared for: {doc.clientName || doc.clientEmail}
        </p>
        <p className="public-meta public-meta--sub">
          Signing page on {window.location.origin}
        </p>

        <ContentSwitcher
          selectedIndex={previewMode}
          onChange={({ index }) => setPreviewMode(index)}
          size="sm"
          className="contract-preview-switcher"
        >
          <Switch name="text" text="Text preview" />
          <Switch name="pdf" text="PDF preview" />
        </ContentSwitcher>

        {previewMode === 0 ? (
          <pre className="contract-content">{content}</pre>
        ) : (
          <div className="contract-pdf-preview">
            {pdfPreviewUrl ? (
              <iframe title="Contract PDF" src={pdfPreviewUrl} className="contract-pdf-frame" />
            ) : (
              <p className="contract-pdf-empty">PDF preview unavailable</p>
            )}
          </div>
        )}

        {isSigned ? (
          <div className="signature-complete public-sign-complete">
            <CheckmarkFilled size={48} className="signature-check" />
            <h3>Signed by {doc.signerName}</h3>
            {(doc.clientSignature || doc.data?.clientSignature) && (
              <img
                src={doc.clientSignature || doc.data?.clientSignature}
                alt="Signature"
                className="signature-preview-img"
              />
            )}
            <p>Signed on {doc.signedAt ? new Date(doc.signedAt).toLocaleString() : '—'}</p>
            <div className="public-sign-actions">
              <Button kind="primary" renderIcon={DocumentPdf} onClick={downloadSignedPdf}>
                Download signed PDF
              </Button>
            </div>
          </div>
        ) : (
          <div className="public-sign-section">
            <h3>Sign and submit</h3>
            <p>Review the contract above, then add your signature below. The PDF will update when you submit.</p>
            <TextInput
              id="signer-name"
              labelText="Your full name"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              required
            />
            <p className="signature-label">Draw your signature:</p>
            <div className="signature-canvas-container">
              <canvas
                ref={canvasRef}
                width={500}
                height={160}
                className="signature-canvas"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
              />
            </div>
            <div className="public-sign-actions">
              <Button kind="tertiary" onClick={clearSignature}>Clear</Button>
              <Button
                kind="primary"
                renderIcon={Pen}
                onClick={handleSign}
                disabled={!signatureData || !signerName || submitting}
              >
                {submitting ? 'Submitting...' : 'Sign & Submit'}
              </Button>
            </div>
          </div>
        )}
      </Tile>
    </div>
  );
};

export default ContractSignPage;
